import React from "react";
import { Checkbox, FormControl, FormControlLabel, FormHelperText, Radio, RadioGroup, Switch, Typography } from "@mui/material";

import { DEFAULT_DATA_PREP_CONFIGURATION } from "fond/architecture/dataPrepConfiguration";
import { Section, SectionHeading } from "fond/form";
import { DataPrepConfiguration } from "fond/types";

import { CABLE_OPTIONS, CableOption, getCableOptionVariant } from "./cableOptions";

import { OptionCard, OptionDescriptions, OptionIcon } from "./DesignPreferencesTab.styles";

type CablePreference = DataPrepConfiguration["CablePreference"];

interface IProps {
  value: DataPrepConfiguration | null | undefined;
  onChange(value: DataPrepConfiguration): void;
}

const DesignPreferencesTab: React.FC<IProps> = ({ value, onChange }: IProps) => {
  const configuration = value ?? DEFAULT_DATA_PREP_CONFIGURATION;
  const update = (changes: Partial<DataPrepConfiguration>) => onChange({ ...configuration, ...changes });

  return (
    <div>
      <Section>
        <SectionHeading>Road Network</SectionHeading>
        <FormControl fullWidth>
          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={configuration.IncludeServiceRoads}
                onChange={(event) => update({ IncludeServiceRoads: event.target.checked })}
              />
            }
            label="Include service roads"
          />
          <FormHelperText>Service roads represent parking aisles, driveways, alleys, and similar routes.</FormHelperText>
        </FormControl>
      </Section>

      <Section>
        <SectionHeading id="cabling-preference-heading">Cabling Preference</SectionHeading>
        <RadioGroup
          aria-labelledby="cabling-preference-heading"
          value={configuration.CablePreference}
          onChange={(event) => update({ CablePreference: event.target.value as CablePreference })}
          sx={{ flexDirection: "row", flexWrap: "wrap", gap: 2 }}
        >
          {CABLE_OPTIONS.map((option) => (
            <CableOptionCard key={option.preference} option={option} configuration={configuration} onChange={update} />
          ))}
        </RadioGroup>
      </Section>
    </div>
  );
};

interface CableOptionCardProps {
  option: CableOption;
  configuration: DataPrepConfiguration;
  onChange(changes: Partial<DataPrepConfiguration>): void;
}

const CableOptionCard: React.FC<CableOptionCardProps> = ({ option, configuration, onChange }: CableOptionCardProps) => {
  const isSelected = configuration.CablePreference === option.preference;
  const isSubOptionChecked = configuration[option.subOption.key];
  const handleCardClick = () => {
    if (!isSelected) onChange({ CablePreference: option.preference });
  };

  return (
    <OptionCard isSelected={isSelected} onClick={handleCardClick}>
      <FormControlLabel
        value={option.preference}
        control={<Radio color="primary" size="small" />}
        label={<Typography fontWeight={500}>{option.label}</Typography>}
        // Selecting via the radio already fires onChange, so don't let the click reach the card and fire it again.
        onClick={(event) => event.stopPropagation()}
        sx={{ alignSelf: "flex-start" }}
      />
      <OptionIcon>{getCableOptionVariant(option, isSubOptionChecked).icon}</OptionIcon>
      <OptionDescriptions>
        {[false, true].map((subOptionChecked) => (
          <Typography
            key={String(subOptionChecked)}
            variant="body2"
            color="textSecondary"
            sx={{ gridArea: "1 / 1", visibility: subOptionChecked === isSubOptionChecked ? "visible" : "hidden" }}
          >
            {getCableOptionVariant(option, subOptionChecked).description}
          </Typography>
        ))}
      </OptionDescriptions>
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            color="primary"
            checked={isSubOptionChecked}
            onChange={(event) => onChange({ [option.subOption.key]: event.target.checked })}
          />
        }
        label={option.subOption.label}
        slotProps={{ typography: { variant: "body2" } }}
        disabled={!isSelected}
        // A click on an unselected card's disabled sub-option is ignored rather than selecting the card.
        onClick={(event) => event.stopPropagation()}
        sx={{ alignSelf: "flex-start", mt: "auto", pt: 1 }}
      />
    </OptionCard>
  );
};

export default DesignPreferencesTab;
